import { compact, shuffle, uniq } from 'lodash';

import { CreatorsListUser } from 'lib/types/users';
import { abbreviateNum } from 'lib/helpers';
import { getCreatorLink } from 'features/Explore/helpers';
import { humanReadableFromSlug } from 'lib/utils/modelSlugUtils';

const collaborators = (creators: CreatorsListUser[]) => {
  const collabs = compact(creators.map(
    (c) => c.collaborators?.length > 0 && c.collaborators[0],
  ));
  return shuffle([...uniq(collabs)].slice(0, 3));
};

const creatorRoles = (creators: CreatorsListUser[]) => {
  const roles = compact(creators.map(
    (c) => c.roles?.length > 0 && c.roles[0].name,
  ));
  return new Intl.ListFormat().format([...new Set(roles)].slice(0, 3));
};

const creatorLikes = (creators: CreatorsListUser[]) => {
  const likes = compact(creators.map((c) => c.currentLikes));
  return likes.sort((a, b) => a - b);
};

export const creatorSearchSummary = (
  query: any, creators: CreatorsListUser[], count?: number,
) => {
  if (creators.length < 3) {
    return null;
  }
  const firstString = `Discover ${new Intl.NumberFormat().format(count || 0)} ${query.roleName?.toLowerCase() || 'creators'} on Gondola`;
  const locationString = query.geo ? `in ${humanReadableFromSlug(query.geo)}` : '';
  const randomCreators = shuffle(creators);

  const creatorNameString = (
    <>
      like
      {' '}
      {getCreatorLink(randomCreators[0])}
      ,
      {' '}
      {getCreatorLink(randomCreators[1])}
      , and
      {' '}
      {getCreatorLink(randomCreators[2])}
      .
    </>
  );

  const randomCollaborators = collaborators(creators);
  const collaboratorString = (
    <>
      These creators have collaborated with
      {' '}
      {getCreatorLink(randomCollaborators[0])}
      ,
      {' '}
      {getCreatorLink(randomCollaborators[1])}
      , and
      {' '}
      {getCreatorLink(randomCollaborators[2])}
    </>
  );

  const creatorRolesString = ` and have demonstrated expertise in roles such as ${creatorRoles(creators)}. `;

  const likes = creatorLikes(creators);

  const thirdSentence = likes.length > 0
    ? `Ranging from ${abbreviateNum(likes[0], 0)} to ${abbreviateNum(likes[likes.length - 1], 0)} likes on their content, our diverse community has something for everyone. `
    : ' ';
  const finalSentence = 'Explore their portfolios and follow them on Gondola to see more of their work.';

  return (
    <h3 className="text-sm text-center">
      {firstString}
      {' '}
      {locationString}
      {' '}
      {creatorNameString}
      {' '}
      {collaboratorString}
      {' '}
      {creatorRolesString}
      {thirdSentence}
      {finalSentence}
    </h3>
  );
};
